import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { getTrustedTypesPolicy } from '../lib/contentSecurity'

export interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  ogImage?: string
  ogImageAlt?: string
  ogImageWidth?: string
  ogImageHeight?: string
  ogType?: 'website' | 'article' | 'book' | 'profile'
  article?: {
    publishedTime?: string
    modifiedTime?: string
    author?: string
    section?: string
    tags?: string[]
  }
  canonicalPath?: string
  noindex?: boolean
}

/**
 * Hook do kompleksowego zarządzania SEO meta tagami
 * Integruje canonical URLs, Open Graph, Twitter Cards, i structured data
 */
export const useSEO = (props: SEOProps = {}) => {
  const location = useLocation()
  const baseUrl = 'https://wojskaspecjalne.pl'
  
  // Domyślne wartości
  const defaults = {
    title: 'wojskaspecjalne.pl - Platforma Edukacyjna',
    description: 'Platforma edukacyjna rozwijająca samodzielne, zadaniowe, krytyczne myślenie według dorobku Józefa Kosseckiego i Mariana Mazura',
    keywords: 'edukacja, krytyczne myślenie, cybernetyka, metacybernetyka, Józef Kossecki, Marian Mazur',
    ogImage: `${baseUrl}/favicon/wojskaspecjalne-poland.png`,
    ogImageAlt: 'Logo wojskaspecjalne.pl - Platforma edukacyjna krytycznego myślenia',
    ogImageWidth: '1200',
    ogImageHeight: '630',
    ogType: 'website' as const,
  }
  
  const {
    title = defaults.title,
    description = defaults.description,
    keywords = defaults.keywords,
    ogImage = defaults.ogImage,
    ogImageAlt = defaults.ogImageAlt,
    ogImageWidth = defaults.ogImageWidth,
    ogImageHeight = defaults.ogImageHeight,
    ogType = defaults.ogType,
    article,
    canonicalPath,
    noindex = false,
  } = props
  
  useEffect(() => {
    // 1. Title
    document.title = title
    
    // 2. Meta Description
    setOrUpdateMetaTag('description', description)
    
    // 3. Meta Keywords
    setOrUpdateMetaTag('keywords', keywords)
    
    // 4. Canonical URL
    const canonicalUrl = `${baseUrl}${canonicalPath || location.pathname}`
    setOrUpdateLinkTag('canonical', canonicalUrl)
    
    // 5. Robots (noindex)
    if (noindex) {
      setOrUpdateMetaTag('robots', 'noindex, nofollow')
    } else {
      removeMetaTag('robots')
    }
    
    // 6. Open Graph Tags
    setOrUpdateMetaProperty('og:title', title)
    setOrUpdateMetaProperty('og:description', description)
    setOrUpdateMetaProperty('og:url', canonicalUrl)
    setOrUpdateMetaProperty('og:image', ogImage)
    setOrUpdateMetaProperty('og:image:alt', ogImageAlt)
    setOrUpdateMetaProperty('og:image:width', ogImageWidth)
    setOrUpdateMetaProperty('og:image:height', ogImageHeight)
    setOrUpdateMetaProperty('og:type', ogType)
    setOrUpdateMetaProperty('og:site_name', 'wojskaspecjalne.pl')
    setOrUpdateMetaProperty('og:locale', 'pl_PL')
    
    // 7. Twitter Cards
    setOrUpdateMetaTag('twitter:card', 'summary_large_image', 'name')
    setOrUpdateMetaTag('twitter:title', title, 'name')
    setOrUpdateMetaTag('twitter:description', description, 'name')
    setOrUpdateMetaTag('twitter:image', ogImage, 'name')
    setOrUpdateMetaTag('twitter:image:alt', ogImageAlt, 'name')
    
    // 8. Article-specific Open Graph (if article data provided)
    if (article && ogType === 'article') {
      if (article.publishedTime) {
        setOrUpdateMetaProperty('article:published_time', article.publishedTime)
      }
      if (article.modifiedTime) {
        setOrUpdateMetaProperty('article:modified_time', article.modifiedTime)
      }
      if (article.author) {
        setOrUpdateMetaProperty('article:author', article.author)
      }
      if (article.section) {
        setOrUpdateMetaProperty('article:section', article.section)
      }
      if (article.tags) {
        // Remove old tags
        document.querySelectorAll('meta[property="article:tag"]').forEach(el => el.remove())
        // Add new tags
        article.tags.forEach(tag => {
          const meta = document.createElement('meta')
          meta.setAttribute('property', 'article:tag')
          meta.setAttribute('content', tag)
          document.head.appendChild(meta)
        })
      }
    }
    
    // 9. Structured Data (JSON-LD)
    updateStructuredData({
      url: canonicalUrl,
      title,
      description,
      ogImage,
      article,
      ogType,
    })
    
  }, [
    title, 
    description, 
    keywords, 
    ogImage,
    ogImageAlt,
    ogImageWidth,
    ogImageHeight,
    ogType, 
    location.pathname, 
    canonicalPath, 
    noindex,
    article,
  ])
}

/**
 * Helper: Ustaw lub zaktualizuj meta tag
 */
function setOrUpdateMetaTag(
  name: string, 
  content: string, 
  attribute: 'name' | 'property' = 'name'
) {
  let meta = document.querySelector(`meta[${attribute}="${name}"]`)
  
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute(attribute, name)
    document.head.appendChild(meta)
  }
  
  meta.setAttribute('content', content)
}

/**
 * Helper: Ustaw lub zaktualizuj meta property (Open Graph)
 */
function setOrUpdateMetaProperty(property: string, content: string) {
  setOrUpdateMetaTag(property, content, 'property')
}

/**
 * Helper: Usuń meta tag
 */
function removeMetaTag(name: string, attribute: 'name' | 'property' = 'name') {
  const meta = document.querySelector(`meta[${attribute}="${name}"]`)
  if (meta) {
    meta.remove()
  }
}

/**
 * Helper: Ustaw lub zaktualizuj link tag
 */
function setOrUpdateLinkTag(rel: string, href: string) {
  let link = document.querySelector(`link[rel="${rel}"]`)
  
  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', rel)
    document.head.appendChild(link)
  }
  
  link.setAttribute('href', href)
}

/**
 * Helper: Zaktualizuj structured data (JSON-LD)
 */
function updateStructuredData(data: {
  url: string
  title: string
  description: string
  ogImage: string
  ogType: string
  article?: SEOProps['article']
}) {
  // Usuń stary JSON-LD jeśli istnieje
  const existingScript = document.querySelector('script[type="application/ld+json"]#seo-structured-data')
  if (existingScript) {
    existingScript.remove()
  }
  
  // Podstawowe structured data dla organizacji
  const baseStructuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://wojskaspecjalne.pl/#organization',
        name: 'wojskaspecjalne.pl',
        url: 'https://wojskaspecjalne.pl',
        logo: {
          '@type': 'ImageObject',
          url: 'https://wojskaspecjalne.pl/favicon/wojskaspecjalne-poland.png',
        },
        description: 'Platforma edukacyjna rozwijająca samodzielne, zadaniowe, krytyczne myślenie',
        sameAs: [
          // Tu można dodać linki do social media
        ],
      },
      {
        '@type': 'WebSite',
        '@id': 'https://wojskaspecjalne.pl/#website',
        url: 'https://wojskaspecjalne.pl',
        name: 'wojskaspecjalne.pl',
        publisher: {
          '@id': 'https://wojskaspecjalne.pl/#organization',
        },
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://wojskaspecjalne.pl/wyszukiwanie?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  }
  
  // Dodaj WebPage lub Article
  if (data.ogType === 'article' && data.article) {
    baseStructuredData['@graph'].push({
      '@type': 'Article',
      '@id': `${data.url}#article`,
      headline: data.title,
      description: data.description,
      image: data.ogImage,
      datePublished: data.article.publishedTime,
      dateModified: data.article.modifiedTime,
      author: data.article.author ? {
        '@type': 'Person',
        name: data.article.author,
      } : undefined,
      publisher: {
        '@id': 'https://wojskaspecjalne.pl/#organization',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': data.url,
      },
    })
  } else {
    baseStructuredData['@graph'].push({
      '@type': 'WebPage',
      '@id': `${data.url}#webpage`,
      url: data.url,
      name: data.title,
      description: data.description,
      isPartOf: {
        '@id': 'https://wojskaspecjalne.pl/#website',
      },
      about: {
        '@id': 'https://wojskaspecjalne.pl/#organization',
      },
    })
  }
  
  // Dodaj nowy JSON-LD script
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.id = 'seo-structured-data'
  
  // Use Trusted Types policy if available (required by CSP)
  const policy = getTrustedTypesPolicy()
  const jsonString = JSON.stringify(baseStructuredData)
  
  if (policy && policy.createScript) {
    script.textContent = policy.createScript(jsonString) as unknown as string
  } else {
    // Fallback for browsers without Trusted Types support
    script.textContent = jsonString
  }
  
  document.head.appendChild(script)
}

